import React, { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { StripeService } from '../services/stripeService'
import { SupabaseFunctionsService } from '../services/supabaseFunctionsService'

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

const PaymentForm = ({ service, amount, onSuccess, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [succeeded, setSucceeded] = useState(false)
  const [currentStep, setCurrentStep] = useState('registration') // 'registration' or 'payment'
  const [processingStep, setProcessingStep] = useState(null) // 'registration', 'payment', or null
  const [createdRegistration, setCreatedRegistration] = useState(null) // Store the created registration
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    emergencyContact: '',
    emergencyPhone: '',
    dietaryRestrictions: '',
    medicalConditions: '',
    experienceLevel: '',
    expectations: '',
    agreeToTerms: false
  })
  const [registrationErrors, setRegistrationErrors] = useState({})

  const validateRegistration = () => {
    const errors = {}
    
    if (!registrationData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!registrationData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (!registrationData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(registrationData.email)) {
      errors.email = 'Email is invalid'
    }
    if (!registrationData.phone.trim()) {
      errors.phone = 'Phone number is required'
    }
    if (!registrationData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required'
    }
    if (!registrationData.emergencyContact.trim()) {
      errors.emergencyContact = 'Emergency contact is required'
    }
    if (!registrationData.emergencyPhone.trim()) {
      errors.emergencyPhone = 'Emergency contact phone is required'
    }
    if (!registrationData.experienceLevel) {
      errors.experienceLevel = 'Please select your experience level'
    }
    if (!registrationData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms and conditions'
    }
    
    setRegistrationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleRegistrationSubmit = async (event) => {
    event.preventDefault()
    
    if (!validateRegistration()) {
      return
    }

    setLoading(true)
    setError(null)
    setProcessingStep('registration')

    try {
      // Create registration first
      const registrationResult = await SupabaseFunctionsService.registerUser({
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        email: registrationData.email,
        phone: registrationData.phone,
        dateOfBirth: registrationData.dateOfBirth,
        emergencyContactName: registrationData.emergencyContact,
        emergencyContactPhone: registrationData.emergencyPhone,
        dietaryRestrictions: registrationData.dietaryRestrictions,
        medicalConditions: registrationData.medicalConditions,
        experienceLevel: registrationData.experienceLevel,
        retreatId: service.id
      })

      if (registrationResult.success) {
        // Store the created registration
        setCreatedRegistration(registrationResult.registration)
        setCurrentStep('payment')
      } else {
        setError(`Registration failed: ${registrationResult.error}`)
      }
    } catch (err) {
      console.error('Registration error:', err)
      setError('Failed to create registration. Please try again.')
    } finally {
      setLoading(false)
      setProcessingStep(null)
    }
  }

  const handleInputChange = (field, value) => {
    setRegistrationData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error when user starts typing
    if (registrationErrors[field]) {
      setRegistrationErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    
    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.')
      return
    }

    if (!createdRegistration) {
      setError('Registration not found. Please go back and complete registration.')
      return
    }

    setLoading(true)
    setError(null)
    setProcessingStep('payment')

    try {
      // Process payment for the existing registration
      const result = await StripeService.processPaymentForRegistration(
        stripe,
        elements,
        registrationData,
        createdRegistration.id,
        amount,
        service.id
      )

      if (result.success) {
        setSucceeded(true)
        setTimeout(() => {
          onSuccess(result.paymentIntent, registrationData, result.registration)
        }, 2000)
      } else {
        throw new Error(result.error)
      }

    } catch (err) {
      console.error('Payment error:', err)
      setError(`Payment failed: ${err.message}\n\nYour registration has been saved. Please contact support to complete your payment.`)
    } finally {
      setLoading(false)
      setProcessingStep(null)
    }
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        fontFamily: '"Inter", sans-serif',
      },
      invalid: {
        color: '#9e2146',
      },
    },
  }

  if (succeeded) {
    return (
      <div className="payment-success">
        <div className="success-icon">
          <i className="fas fa-check-circle"></i>
        </div>
        <h3>Registration & Payment Successful!</h3>
        <p>Thank you for registering and completing your payment. You will receive a confirmation email with all the details shortly. No account creation required - your registration is complete!</p>
      </div>
    )
  }

  return (
    <div className="payment-form-container">
      <div className="form-header">
        <h3>{currentStep === 'registration' ? 'Complete Your Registration' : 'Complete Your Payment'}</h3>
        <div className="service-summary">
          <h4>{service.title}</h4>
          <p className="amount">${amount}</p>
        </div>
        <div className="form-steps">
          <div className={`step ${currentStep === 'registration' ? 'active' : 'completed'}`}>
            <span className="step-number">1</span>
            <span className="step-label">Registration</span>
          </div>
          <div className={`step ${currentStep === 'payment' ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Payment</span>
          </div>
        </div>
      </div>

      {currentStep === 'registration' ? (
        <form onSubmit={handleRegistrationSubmit} className="registration-form">
          <div className="form-section">
            <h4>Personal Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  value={registrationData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={registrationErrors.firstName ? 'error' : ''}
                />
                {registrationErrors.firstName && <span className="error-text">{registrationErrors.firstName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  value={registrationData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={registrationErrors.lastName ? 'error' : ''}
                />
                {registrationErrors.lastName && <span className="error-text">{registrationErrors.lastName}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={registrationData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={registrationErrors.email ? 'error' : ''}
                />
                {registrationErrors.email && <span className="error-text">{registrationErrors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  value={registrationData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={registrationErrors.phone ? 'error' : ''}
                />
                {registrationErrors.phone && <span className="error-text">{registrationErrors.phone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth *</label>
              <input
                type="date"
                id="dateOfBirth"
                value={registrationData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                className={registrationErrors.dateOfBirth ? 'error' : ''}
              />
              {registrationErrors.dateOfBirth && <span className="error-text">{registrationErrors.dateOfBirth}</span>}
            </div>
          </div>

          <div className="form-section">
            <h4>Emergency Contact</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="emergencyContact">Emergency Contact Name *</label>
                <input
                  type="text"
                  id="emergencyContact"
                  value={registrationData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  className={registrationErrors.emergencyContact ? 'error' : ''}
                />
                {registrationErrors.emergencyContact && <span className="error-text">{registrationErrors.emergencyContact}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="emergencyPhone">Emergency Contact Phone *</label>
                <input
                  type="tel"
                  id="emergencyPhone"
                  value={registrationData.emergencyPhone}
                  onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                  className={registrationErrors.emergencyPhone ? 'error' : ''}
                />
                {registrationErrors.emergencyPhone && <span className="error-text">{registrationErrors.emergencyPhone}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4>Health & Experience</h4>
            <div className="form-group">
              <label htmlFor="experienceLevel">Experience Level with Plant Medicine *</label>
              <select
                id="experienceLevel"
                value={registrationData.experienceLevel}
                onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                className={registrationErrors.experienceLevel ? 'error' : ''}
              >
                <option value="">Select your experience level</option>
                <option value="beginner">Beginner - No experience</option>
                <option value="some">Some experience (1-3 ceremonies)</option>
                <option value="experienced">Experienced (4+ ceremonies)</option>
              </select>
              {registrationErrors.experienceLevel && <span className="error-text">{registrationErrors.experienceLevel}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="dietaryRestrictions">Dietary Restrictions</label>
              <textarea
                id="dietaryRestrictions"
                value={registrationData.dietaryRestrictions}
                onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                placeholder="Please list any dietary restrictions or allergies..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="medicalConditions">Medical Conditions</label>
              <textarea
                id="medicalConditions"
                value={registrationData.medicalConditions}
                onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                placeholder="Please list any medical conditions, medications, or health concerns..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expectations">What are your intentions for this retreat?</label>
              <textarea
                id="expectations"
                value={registrationData.expectations}
                onChange={(e) => handleInputChange('expectations', e.target.value)}
                placeholder="Share your goals, intentions, or what you hope to gain from this experience..."
                rows="4"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={registrationData.agreeToTerms}
                  onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                  className={registrationErrors.agreeToTerms ? 'error' : ''}
                />
                <span className="checkmark"></span>
                &nbsp; I agree to the <a href="/terms" target="_blank">Terms and Conditions</a> *
              </label>
              {registrationErrors.agreeToTerms && <span className="error-text">{registrationErrors.agreeToTerms}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Creating Registration...
                </>
              ) : (
                <>
                  Continue to Payment
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="payment-form">
          <div className="card-element-container">
            <label htmlFor="card-element">Card Information</label>
            <CardElement
              id="card-element"
              options={cardElementOptions}
            />
          </div>

          {processingStep && (
            <div className="processing-message">
              <i className="fas fa-spinner fa-spin"></i>
              {processingStep === 'registration' && 'Saving your registration...'}
              {processingStep === 'payment' && 'Processing payment...'}
            </div>
          )}

          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <div className="payment-actions">
            <button
              type="button"
              onClick={() => setCurrentStep('registration')}
              className="btn btn-secondary"
              disabled={loading}
            >
              <i className="fas fa-arrow-left"></i>
              Back to Registration
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!stripe || loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-lock"></i>
                  Pay ${amount}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      <div className="payment-security">
        <i className="fas fa-shield-alt"></i>
        <span>Your information is secure and encrypted</span>
      </div>
    </div>
  )
}

const PaymentFormWrapper = ({ service, amount, onSuccess, onCancel }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        service={service}
        amount={amount}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  )
}

export default PaymentFormWrapper
